import React, { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import useAuthStore from "../../store/authStore";
import apiClient from "../../api/apiClient";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const TIMEZONES = [
  { value: "Asia/Kolkata",         label: "Asia/Kolkata           (UTC +5:30)"   },
  { value: "Asia/Dubai",           label: "Asia/Dubai             (UTC +4)"      },
  { value: "Asia/Karachi",         label: "Asia/Karachi           (UTC +5)"      },
  { value: "Asia/Dhaka",           label: "Asia/Dhaka             (UTC +6)"      },
  { value: "Asia/Colombo",         label: "Asia/Colombo           (UTC +5:30)"   },
  { value: "Asia/Kathmandu",       label: "Asia/Kathmandu         (UTC +5:45)"   },
  { value: "Asia/Singapore",       label: "Asia/Singapore         (UTC +8)"      },
  { value: "Asia/Tokyo",           label: "Asia/Tokyo             (UTC +9)"      },
  { value: "Asia/Seoul",           label: "Asia/Seoul             (UTC +9)"      },
  { value: "Asia/Shanghai",        label: "Asia/Shanghai          (UTC +8)"      },
  { value: "Asia/Bangkok",         label: "Asia/Bangkok           (UTC +7)"      },
  { value: "Asia/Jakarta",         label: "Asia/Jakarta           (UTC +7)"      },
  { value: "Asia/Riyadh",          label: "Asia/Riyadh            (UTC +3)"      },
  { value: "Asia/Kuwait",          label: "Asia/Kuwait            (UTC +3)"      },
  { value: "Asia/Tehran",          label: "Asia/Tehran            (UTC +3:30)"   },
  { value: "Asia/Baghdad",         label: "Asia/Baghdad           (UTC +3)"      },
  { value: "Asia/Jerusalem",       label: "Asia/Jerusalem         (UTC +2/+3)"   },
  { value: "Asia/Tashkent",        label: "Asia/Tashkent          (UTC +5)"      },
  { value: "Australia/Sydney",     label: "Australia/Sydney       (UTC +10/+11)" },
  { value: "Australia/Perth",      label: "Australia/Perth        (UTC +8)"      },
  { value: "Australia/Brisbane",   label: "Australia/Brisbane     (UTC +10)"     },
  { value: "Pacific/Auckland",     label: "Pacific/Auckland       (UTC +12/+13)" },
  { value: "Pacific/Honolulu",     label: "Pacific/Honolulu       (UTC −10)"     },
  { value: "Europe/London",        label: "Europe/London          (UTC 0/+1)"    },
  { value: "Europe/Paris",         label: "Europe/Paris           (UTC +1/+2)"   },
  { value: "Europe/Berlin",        label: "Europe/Berlin          (UTC +1/+2)"   },
  { value: "Europe/Moscow",        label: "Europe/Moscow          (UTC +3)"      },
  { value: "Europe/Istanbul",      label: "Europe/Istanbul        (UTC +3)"      },
  { value: "Africa/Cairo",         label: "Africa/Cairo           (UTC +2)"      },
  { value: "Africa/Lagos",         label: "Africa/Lagos           (UTC +1)"      },
  { value: "Africa/Nairobi",       label: "Africa/Nairobi         (UTC +3)"      },
  { value: "Africa/Johannesburg",  label: "Africa/Johannesburg    (UTC +2)"      },
  { value: "America/New_York",     label: "America/New_York       (UTC −5/−4)"   },
  { value: "America/Chicago",      label: "America/Chicago        (UTC −6/−5)"   },
  { value: "America/Denver",       label: "America/Denver         (UTC −7/−6)"   },
  { value: "America/Los_Angeles",  label: "America/Los_Angeles    (UTC −8/−7)"   },
  { value: "America/Toronto",      label: "America/Toronto        (UTC −5/−4)"   },
  { value: "America/Sao_Paulo",    label: "America/Sao_Paulo      (UTC −3/−2)"   },
  { value: "America/Buenos_Aires", label: "America/Buenos_Aires   (UTC −3)"      },
  { value: "America/Mexico_City",  label: "America/Mexico_City    (UTC −6/−5)"   },
  { value: "UTC",                  label: "UTC                    (UTC 0)"       },
];

function UpdateLocations() {
  const navigate = useNavigate();
  const loggedInId = useAuthStore((state) => state.employeeId);
  const role = useAuthStore((state) => state.role);
  const [isSuperAdmin, setIsSuperAdmin] = useState(null);

  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Form State
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [allowedRadius, setAllowedRadius] = useState(100);
  const [allowedIps, setAllowedIps] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    async function verifyRole() {
      if (role !== "ADMIN" || !loggedInId) {
        setIsSuperAdmin(false);
        return;
      }
      try {
        const res = await apiClient.get(`/accounts/api/users/${loggedInId}/`);
        const isSuper = res.data.user?.is_superuser === true;
        setIsSuperAdmin(isSuper);
        if (!isSuper) {
          alert("Access Denied: Only Super Admin can access this page.");
          navigate("/admin/dashboard");
        }
      } catch {
        setIsSuperAdmin(false);
        navigate("/admin/dashboard");
      }
    }
    verifyRole();
  }, [role, loggedInId, navigate]);

  useEffect(() => {
    if (isSuperAdmin === true) {
      loadLocations();
    }
  }, [isSuperAdmin]);

  async function loadLocations() {
    try {
      setLoading(true);
      const res = await apiClient.get("/accounts/api/locations/");
      setLocations(res.data.locations || []);
    } catch (err) {
      console.error("Failed to load locations:", err);
    } finally {
      setLoading(false);
    }
  }

  function handleSelect(loc) {
    setSelectedLoc(loc);
    setName(loc.name || "");
    setTimezone(loc.timezone || "Asia/Kolkata");
    setLatitude(loc.latitude !== null && loc.latitude !== undefined ? loc.latitude : "");
    setLongitude(loc.longitude !== null && loc.longitude !== undefined ? loc.longitude : "");
    setAllowedRadius(loc.allowed_radius_meters || 100);
    setAllowedIps(loc.allowed_ips || "");
    setErrors({});
  }

  function handleDetectGPS() {
    if (!navigator.geolocation) {
      setErrors((p) => ({ ...p, latitude: "Geolocation not supported." }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude.toFixed(6));
        setLongitude(pos.coords.longitude.toFixed(6));
        setErrors((p) => ({ ...p, latitude: "", longitude: "" }));
      },
      () => setErrors((p) => ({ ...p, latitude: "Could not detect location. Check browser permissions." }))
    );
  }

  function validate() {
    const errs = {};
    if (!name.trim()) errs.name = "Location name is required.";
    const latNum = latitude !== "" ? parseFloat(latitude) : null;
    const lngNum = longitude !== "" ? parseFloat(longitude) : null;

    if (latitude !== "" && (isNaN(latNum) || latNum < -90 || latNum > 90)) {
      errs.latitude = "Must be between -90 and 90.";
    }
    if (longitude !== "" && (isNaN(lngNum) || lngNum < -180 || lngNum > 180)) {
      errs.longitude = "Must be between -180 and 180.";
    }
    if ((latitude === "") !== (longitude === "")) {
      errs.latitude = "Provide both GPS values or neither.";
      errs.longitude = "Provide both GPS values or neither.";
    }
    if (allowedRadius !== "" && parseInt(allowedRadius) < 1) {
      errs.allowedRadius = "Radius must be at least 1 meter.";
    }
    return errs;
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!selectedLoc) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        name: name.trim(),
        timezone,
        allowed_radius_meters: parseInt(allowedRadius),
        allowed_ips: allowedIps,
        latitude: latitude !== "" ? parseFloat(latitude) : null,
        longitude: longitude !== "" ? parseFloat(longitude) : null,
      };

      await apiClient.patch(`/accounts/api/locations/${selectedLoc.code}/`, payload);
      alert("Location updated successfully! ✅");
      setSelectedLoc(null);
      loadLocations();
    } catch (err) {
      setErrors({
        api: err.response?.data?.error || err.response?.data?.detail || "Failed to update location.",
      });
    } finally {
      setSaveLoading(false);
    }
  }

  if (isSuperAdmin === null || isSuperAdmin === false) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-white/60 text-sm">Verifying access...</p>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6">
        <div className="max-w-6xl mx-auto space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg text-xl">
                🌍
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">Update Locations</h1>
                <p className="text-white/50 text-xs">Configure geofence coordinates and network IP restrictions</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/create-user")}
              className="px-3.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-semibold transition"
            >
              ← Back
            </button>
          </div>

          {/* Main Content Area: Split layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            {/* Left side: Locations list */}
            <div className="lg:col-span-5 bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-5 space-y-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-white/40 mb-1">Select Location</h2>
              {loading ? (
                <div className="p-6 text-center text-white/40 text-xs">Loading locations...</div>
              ) : locations.length === 0 ? (
                <div className="p-6 text-center text-white/40 text-xs">No locations configured.</div>
              ) : (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                  {locations.map((loc) => {
                    const isSelected = selectedLoc?.code === loc.code;
                    return (
                      <div
                        key={loc.code}
                        onClick={() => handleSelect(loc)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer flex flex-col gap-1.5 ${
                          isSelected
                            ? "bg-violet-500/20 border-violet-400/50 shadow-md shadow-violet-500/5"
                            : "bg-white/5 hover:bg-white/8 border-white/10 hover:border-white/15"
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold text-xs sm:text-sm text-white">{loc.name}</h3>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-white/15 text-white/80">
                            {loc.code}
                          </span>
                        </div>
                        <div className="text-[10px] text-white/50 flex flex-wrap items-center gap-2">
                          <span>🕒 {loc.timezone}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border ${
                            loc.latitude
                              ? "bg-green-500/25 text-green-400 border-green-500/20"
                              : "bg-white/5 text-white/30 border-white/10"
                          }`}>
                            🌍 {loc.latitude ? `${loc.allowed_radius_meters}m GPS` : "No GPS"}
                          </span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border ${
                            loc.allowed_ips
                              ? "bg-blue-500/25 text-blue-400 border-blue-500/20"
                              : "bg-white/5 text-white/30 border-white/10"
                          }`}>
                            💻 {loc.allowed_ips ? "IP Whitelist" : "No IP Gate"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right side: Editor Form */}
            <div className="lg:col-span-7">
              <AnimatePresence mode="wait">
                {selectedLoc ? (
                  <motion.div
                    key={selectedLoc.code}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-5 space-y-4"
                  >
                    <div className="border-b border-white/10 pb-3 flex items-center justify-between">
                      <h2 className="text-sm sm:text-base font-bold text-white">
                        Edit {selectedLoc.name}
                      </h2>
                      <span className="text-xs font-semibold text-violet-400">
                        {selectedLoc.code}
                      </span>
                    </div>

                    <form onSubmit={handleSave} className="space-y-4">
                      {/* Name */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-white font-semibold text-xs">
                          Location Name
                        </label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => { setName(e.target.value); setErrors(p => ({ ...p, name: "" })); }}
                          className={`w-full bg-white/10 border ${
                            errors.name ? "border-red-400" : "border-white/30 hover:border-white/50"
                          } focus:border-violet-400 rounded-xl px-3.5 py-2 text-white text-xs outline-none transition`}
                        />
                        {errors.name && <span className="text-red-400 text-[10px]">{errors.name}</span>}
                      </div>

                      {/* Timezone */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-white font-semibold text-xs">
                          Timezone
                        </label>
                        <select
                          value={timezone}
                          onChange={(e) => setTimezone(e.target.value)}
                          className="w-full bg-slate-900 border border-white/30 hover:border-white/50 focus:border-violet-400 rounded-xl px-3 py-2 text-white text-xs outline-none transition"
                        >
                          {TIMEZONES.map((tz) => (
                            <option key={tz.value} value={tz.value} className="bg-slate-900">
                              {tz.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* GPS Restriction Grid */}
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3.5 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="text-green-400 text-sm">🌍</span>
                            <span className="text-white font-semibold text-xs">GPS Geofence</span>
                          </div>
                          <button
                            type="button"
                            onClick={handleDetectGPS}
                            className="px-2 py-0.5 text-[10px] font-bold rounded bg-green-500/20 border border-green-500/35 hover:bg-green-500/30 text-green-400 transition"
                          >
                            Detect Current GPS
                          </button>
                        </div>
                        <p className="text-white/40 text-[10px]">Leave blank to skip GPS verification.</p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="flex flex-col gap-1.5">
                            <label className="text-white/60 font-semibold text-[10px] uppercase">Latitude</label>
                            <input
                              type="text"
                              value={latitude}
                              onChange={(e) => { setLatitude(e.target.value); setErrors(p => ({ ...p, latitude: "", longitude: "" })); }}
                              placeholder="e.g. 17.3850"
                              className={`w-full bg-white/10 border ${
                                errors.latitude ? "border-red-400" : "border-white/30 hover:border-white/50"
                              } focus:border-violet-400 rounded-xl px-3 py-2 text-white text-xs outline-none transition`}
                            />
                            {errors.latitude && <span className="text-red-400 text-[10px]">{errors.latitude}</span>}
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="text-white/60 font-semibold text-[10px] uppercase">Longitude</label>
                            <input
                              type="text"
                              value={longitude}
                              onChange={(e) => { setLongitude(e.target.value); setErrors(p => ({ ...p, latitude: "", longitude: "" })); }}
                              placeholder="e.g. 78.4867"
                              className={`w-full bg-white/10 border ${
                                errors.longitude ? "border-red-400" : "border-white/30 hover:border-white/50"
                              } focus:border-violet-400 rounded-xl px-3 py-2 text-white text-xs outline-none transition`}
                            />
                            {errors.longitude && <span className="text-red-400 text-[10px]">{errors.longitude}</span>}
                          </div>
                        </div>

                        {/* Radius */}
                        <div className="flex flex-col gap-1.5">
                          <label className="text-white/60 font-semibold text-[10px] uppercase">
                            Allowed Radius (metres)
                          </label>
                          <input
                            type="number"
                            value={allowedRadius}
                            onChange={(e) => { setAllowedRadius(e.target.value); setErrors(p => ({ ...p, allowedRadius: "" })); }}
                            className={`w-full bg-white/10 border ${
                              errors.allowedRadius ? "border-red-400" : "border-white/30 hover:border-white/50"
                            } focus:border-violet-400 rounded-xl px-3 py-2 text-white text-xs outline-none transition`}
                          />
                          {errors.allowedRadius && <span className="text-red-400 text-[10px]">{errors.allowedRadius}</span>}
                        </div>
                      </div>

                      {/* IP Restriction */}
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3.5 space-y-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-blue-400 text-sm">💻</span>
                          <span className="text-white font-semibold text-xs">IP Whitelist</span>
                        </div>
                        <p className="text-white/40 text-[10px]">Leave blank to allow any network connection. Separate with commas.</p>
                        <div className="flex flex-col gap-1.5">
                          <textarea
                            value={allowedIps}
                            onChange={(e) => setAllowedIps(e.target.value)}
                            placeholder="e.g. 192.168.1.10, 10.0.0.5"
                            rows={2}
                            className="w-full bg-white/10 border border-white/30 hover:border-white/50 focus:border-violet-400 rounded-xl px-3 py-2 text-white text-xs outline-none transition resize-none placeholder:text-white/30"
                          />
                        </div>
                      </div>

                      {errors.api && <p className="text-red-400 text-xs font-semibold">{errors.api}</p>}

                      {/* Action buttons */}
                      <div className="flex gap-2.5 pt-1">
                        <button
                          type="button"
                          onClick={() => setSelectedLoc(null)}
                          className="flex-1 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold text-xs transition"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saveLoading}
                          className="flex-1 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold text-xs transition shadow-lg disabled:opacity-55"
                        >
                          {saveLoading ? "Saving..." : "Update Location"}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                ) : (
                  <div className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 p-8 text-center text-white/40 shadow-2xl">
                    <p className="text-base mb-1">🌍 Location Editor</p>
                    <p className="text-[11px]">Select an office location from the list on the left to start editing its settings.</p>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default UpdateLocations;
